import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, where, orderBy, Timestamp, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';

const TIME_TRACKING_COLLECTION = 'timeTracking';

export const createTimeEntry = async (timeEntryData) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to create time entries');
    }

    // Get user document to get the user's name
    const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
    if (!userDoc.exists()) {
      throw new Error('User document not found');
    }
    const userData = userDoc.data();

    // Format the data for Firestore
    const formattedData = {
      userId: auth.currentUser.uid,
      userName: `${userData.firstName} ${userData.lastName}`,
      accountId: timeEntryData.accountId || 'internal',
      accountName: timeEntryData.accountName || 'BBA INTERNAL (NON CLIENT)',
      clockIn: Timestamp.fromDate(new Date(timeEntryData.clockIn)),
      clockOut: Timestamp.fromDate(new Date(timeEntryData.clockOut)),
      hours: (timeEntryData.clockOut - timeEntryData.clockIn) / 3600000, // Convert milliseconds to hours
      notes: timeEntryData.notes || '',
      createdAt: serverTimestamp()
    };

    // Add the document to Firestore
    const docRef = await addDoc(collection(db, TIME_TRACKING_COLLECTION), formattedData);
    
    // Return the created time entry with proper date formatting
    return {
      id: docRef.id,
      ...formattedData,
      clockIn: formattedData.clockIn.toDate(),
      clockOut: formattedData.clockOut.toDate(),
      createdAt: new Date()
    };
  } catch (error) {
    console.error('Error creating time entry:', error);
    throw error;
  }
};

export const getTimeEntries = async (filters = {}) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to get time entries');
    }

    const timeEntriesRef = collection(db, TIME_TRACKING_COLLECTION);
    let q = query(timeEntriesRef);

    // Apply filters
    if (filters.userId) {
      q = query(q, where('userId', '==', filters.userId));
    }
    if (filters.accountId) {
      q = query(q, where('accountId', '==', filters.accountId));
    }
    if (filters.startDate) {
      q = query(q, where('clockIn', '>=', Timestamp.fromDate(new Date(filters.startDate))));
    }
    if (filters.endDate) {
      q = query(q, where('clockOut', '<=', Timestamp.fromDate(new Date(filters.endDate))));
    }

    // Always order by clock in time, most recent first
    q = query(q, orderBy('clockIn', 'desc'));

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      clockIn: doc.data().clockIn.toDate(),
      clockOut: doc.data().clockOut.toDate(),
      createdAt: doc.data().createdAt.toDate()
    }));
  } catch (error) {
    console.error('Error getting time entries:', error);
    throw error;
  }
};

export const updateTimeEntry = async (timeEntryId, timeEntryData) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to update time entries');
    }

    const timeEntryRef = doc(db, TIME_TRACKING_COLLECTION, timeEntryId);
    
    // Format the data for Firestore
    const formattedData = {
      ...timeEntryData,
      clockIn: Timestamp.fromDate(new Date(timeEntryData.clockIn)),
      clockOut: Timestamp.fromDate(new Date(timeEntryData.clockOut)),
      hours: (timeEntryData.clockOut - timeEntryData.clockIn) / 3600000,
      updatedAt: serverTimestamp()
    };

    await updateDoc(timeEntryRef, formattedData);
    return { id: timeEntryId, ...formattedData };
  } catch (error) {
    console.error('Error updating time entry:', error);
    throw error;
  }
};

export const deleteTimeEntry = async (timeEntryId) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to delete time entries');
    }

    const timeEntryRef = doc(db, TIME_TRACKING_COLLECTION, timeEntryId);
    await deleteDoc(timeEntryRef);
    return timeEntryId;
  } catch (error) {
    console.error('Error deleting time entry:', error);
    throw error;
  }
}; 